import { useState, useCallback, useRef, useEffect } from "react";
import { useDialogRegistry } from "./DialogRegistry";

export interface DialogWithDataProps<TData> {
  data: TData | null;
  open: boolean;
  onClose: () => void;
  onTransitionExited: () => void;
}

export function useDialogWithData<TData>() {
  const [data, setData] = useState<TData | null>(null);
  const { registerDialog } = useDialogRegistry();

  const closeDialog = useCallback(() => {
    setData(null);
  }, [setData]);

  useEffect(() => {
    return registerDialog(closeDialog);
  }, [registerDialog, closeDialog]);

  const openDialog = useCallback((data: TData) => {
    setData(data);
  }, []);

  const afterCloseRef = useRef<null | (() => void)>(null);
  const onTransitionExited = useCallback(() => {
    if (afterCloseRef.current) {
      afterCloseRef.current();
      afterCloseRef.current = null;
    }
  }, []);

  const closeThen = useCallback(
    (afterClose: () => void) => {
      afterCloseRef.current = afterClose;
      closeDialog();
    },
    [closeDialog],
  );

  const open = Boolean(data);

  const dialogProps: DialogWithDataProps<TData> = {
    data,
    open,
    onClose: closeDialog,
    onTransitionExited,
  };

  return {
    closeDialog,
    closeThen,
    onTransitionExited,
    open,
    openDialog,
    dialogProps,
  };
}

// for simple dialogs that don't need to pass data to the dialog
export function useDialog() {
  const [open, setOpen] = useState<boolean>(false);
  const { registerDialog } = useDialogRegistry();

  const closeDialog = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  useEffect(() => {
    return registerDialog(closeDialog);
  }, [registerDialog, closeDialog]);

  const openDialog = useCallback(() => {
    setOpen(true);
  }, [setOpen]);

  const afterCloseRef = useRef<null | (() => void)>(null);
  const onTransitionExited = useCallback(() => {
    if (afterCloseRef.current) {
      afterCloseRef.current();
      afterCloseRef.current = null;
    }
  }, []);

  const closeThen = useCallback(
    (afterClose: () => void) => {
      afterCloseRef.current = afterClose;
      closeDialog();
    },
    [closeDialog],
  );

  return {
    closeDialog,
    closeThen,
    onTransitionExited,
    open,
    openDialog,
    dialogProps: {
      open,
      onClose: closeDialog,
      onTransitionExited,
    },
  };
}
